<template>
  <div class="favorites profile-content">
    <!-- Error -->
    <div
      v-if="state === 'error'"
      class="favorites__error"
    >
      <p>{{ $options.text.error }}</p>
    </div>

    <!-- Content -->
    <template v-else>
      <!-- Header -->
      <div
        v-if="isProfileFavoritesHeader"
        class="favorites__header"
      >
        <ProfileFavoritesHeader
          :folders="folders"
          :folder-current="folderCurrent"
          @choose-folder="onChooseFolder"
          @create-folder="onCreateFolder"
        />
      </div>

      <!-- No Data -->
      <div
        v-if="state === 'no-data'"
        class="favorites__no-data"
      >
        <div class="lottie-animation-container">
          <BannerNoData
            :title="noDataTitle"
            animation-loader="~/assets/lottie/lazeika-favorites.json"
          />
        </div>
        <a
          v-if="!posts.length && folderCurrentId === 0"
          class="favorites__select-articles-button"
          href="/"
        >
          {{ selectArticlesText }}
        </a>
        <div
          v-else
          class="favorites__no-data-subtitle"
        >
          {{ selectArticlesText }}
        </div>
      </div>

      <!-- Content -->
      <div
        v-else
        class="favorites__content"
      >
        <div
          v-for="(article, index) in posts"
          :key="article.id"
          class="posts__item"
        >
          <ArticleCardSmall
            v-bind="useArticleProps(article)"
            :loading-views="getLoadingViews(article.id)"
            cover-size="small"
            @click-on-article-card="onClickArticleCard(article, index + 1)"
            @click-on-restore-button="updateViewsCount(article.id)"
            @favorite-delete="
              (favorite) => {
                onFavoriteDelete(favorite, article, false, updateDelete);
              }
            "
            @favorite-create-folder-and-add="
              (nameFolder) => onFavoriteCreateFolderAndAdd(nameFolder, article, true, update)
            "
            @favorite-create-folder-and-transfer="
              (nameFolder, favorite) => {
                onFavoriteCreateFolderAndTransfer(nameFolder, favorite, article, update);
              }
            "
            @favorite-transfer-to-folder="
              (favorite, folder) => onFavoriteTransferToFolder(favorite, folder, article, update)
            "
            @favorite-add-to-folder="(folder) => onFavoriteAddToFolder(folder, article)"
            @favorite-deferred="onFavoriteDeferred"
          />
        </div>

        <!-- Placeholder -->
        <template v-if="state === 'fetching'">
          <ArticleCardSmallPlaceholder />
        </template>

        <!-- Observer Load List -->
        <IntersectionObserver
          v-if="state === 'pending'"
          ref="observer"
          @appear="fetchFavoritesItemsByObserve"
        />
      </div>
    </template>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import { parseData } from '@devhacker/shared/utils/parseData';
  import {
    PROFILE_MAX_PER_PAGE,
    type FavoritesParams,
  } from '@devhacker/shared/features/profile/api';
  import IntersectionObserver from '@/components/lh-ui/IntersectionObserver/IntersectionObserver.vue';
  import ArticleCardSmall from '@/components/lh-ui/ArticleCard/ArticleCardSmall/ArticleCardSmall.vue';

  import { IronMaidenListSchema } from '~/schemas/ironMaiden';
  import { trimWpDomainInPlace } from '~/utils/trimWpDomain';
  import { useArticlesStore } from '~/store/articles';
  import { useFoldersStore } from '~/store/folders';
  import ProfileFavoritesHeader from '~/components/ProfileFavoritesHeader.vue';
  import { useFavorite } from '~/composables/useFavoriteMethods';
  import { useUpdatingViewsCount } from '~/composables/useUpdatingViewsCount';
  import { useArticleProps } from '~/composables/useArticleProps';

  const BannerNoData = defineAsyncComponent(
    () => import('~/components/BannerNoData/BannerNoData.vue'),
  );
  const ArticleCardSmallPlaceholder = defineAsyncComponent(
    () => import('~/placeholders/ArticleCardSmallPlaceholder/ArticleCardSmallPlaceholder.vue'),
  );

  type ProfileDataFavorites = {
    items: any[];
    posts: any[];
    total: number;
    page: number;
    perPage: number;
    state: 'pending' | 'fetching' | 'no-data' | 'fulfilled' | 'error';
    folderCurrent: IFolderItem | null;
  };

  export default defineNuxtComponent({
    name: 'ProfileFavorites',

    components: {
      ArticleCardSmall,
      IntersectionObserver,
      BannerNoData,
      ArticleCardSmallPlaceholder,
      ProfileFavoritesHeader,
    },

    props: {
      userId: {
        type: String as PropType<string>,
        required: true,
      },
      isOwnProfile: {
        type: Boolean as PropType<boolean>,
        default: false,
      },
    },

    setup() {
      return {
        ...useFavorite(),
        ...useUpdatingViewsCount(),
        articlesStore: useArticlesStore(),
        foldersStore: useFoldersStore(),
        nuxtApp: useNuxtApp(),
      };
    },

    text: {
      error: 'Произошла ошибка при загрузке избранного.',
      selectArticles: 'Выбрать статьи',
      addFirstArticles: 'Добавьте первую!',
    },

    noData: {
      title: 'Нет статей в избранном',

      noDataFavorites: 'Нет статей в избранном',
      noDataFolder: 'Нет статей в папке',
    },

    data(): ProfileDataFavorites {
      return {
        items: [],
        posts: [],
        total: 0,
        state: 'pending',
        page: 0,
        perPage: 10,
        folderCurrent: null,
      };
    },

    computed: {
      folders(): Array<IFolderItem> {
        return this.foldersStore.items;
      },

      folderCurrentId(): number {
        return this.folderCurrent ? (this.folderCurrent.id ?? 0) : 0;
      },

      isProfileFavoritesHeader(): boolean {
        return Boolean(this.total || this.folderCurrentId);
      },

      selectArticlesText(): string {
        const {
          $options: {
            text: { selectArticles, addFirstArticles },
          },
        } = this;
        return this.folderCurrentId ? addFirstArticles : selectArticles;
      },

      noDataTitle(): string {
        const {
          $options: {
            noData: { noDataFavorites, noDataFolder },
          },
        } = this;

        return this.folderCurrentId ? noDataFolder : noDataFavorites;
      },
    },

    watch: {
      folders() {
        this.setFolderCurrent();
      },

      folderCurrent() {
        const { folderCurrent } = this;

        if (!folderCurrent) {
          return;
        }

        this.page = 1;
        this.total = folderCurrent.total;
        this.posts = [];
        this.items = [];
        this.state = 'pending';

        const params: FavoritesParams = {
          page: this.page,
          per_page: this.perPage,
          folder_ids: this.folderCurrentId,
        };

        this.fetchFavoritesItems(params);
      },
    },

    created() {
      this.setFolderCurrent();
    },

    methods: {
      useArticleProps,
      setFolderCurrent() {
        const folderCurrent = this.folders.find((folder: IFolderItem) => folder.id === null);

        if (!this.folderCurrent) {
          this.folderCurrent = folderCurrent ?? null;
        }
      },

      update() {
        if (this.folderCurrentId) {
          this.fetchFavoritesItems(this.loadedPagesParams(), true);
        }
      },

      updateDelete() {
        this.fetchFavoritesItems(this.loadedPagesParams(), true);
      },

      loadedPagesParams(): FavoritesParams {
        const { page, perPage } = this;

        return {
          page: 1,
          per_page: Math.min(page * perPage, PROFILE_MAX_PER_PAGE),
          folder_ids: this.folderCurrentId,
        };
      },

      fetchFavoritesItemsByObserve(): void {
        const { page, perPage, folderCurrentId } = this;
        this.page += 1;

        const params: FavoritesParams = {
          page: page + 1,
          per_page: perPage,
          folder_ids: folderCurrentId,
        };

        this.fetchFavoritesItems(params);
      },

      fetchFavoritesItems(params: FavoritesParams, isReset: boolean = false) {
        this.state = 'fetching';

        if (isReset) {
          this.items = [];
          this.posts = [];
          this.total = 0;
        }

        this.articlesStore
          .fetchAndSetFavorites(params)
          .then((response) => {
            if (!response) {
              console.error('fetchFavorites');
              throw new Error('Error: fetchFavorites');
            }

            this.total = response.meta?.total ?? 0;

            const newItems = response.data.filter(
              (item) => !this.items.some((existing: IFavoriteItem) => existing.id === item.id),
            );

            if (newItems.length) {
              this.items.push(...newItems);
              this.fetchPosts(
                newItems.map((item) => item.post_id),
                isReset,
              );
            } else {
              this.updateState();
            }
          })
          .catch(() => {
            this.state = 'error';
          });
      },

      fetchPosts(includes: Array<number>, isReset: boolean = false): Promise<void> | undefined {
        return this.nuxtApp.$ironMaidenApi
          .fetchPostsByIds(includes)
          .then((response) => {
            if (response.status !== 200 && response.status !== 404) {
              console.error('fetchPostsByIds');
              throw new Error('Error: fetchPostsByIds');
            }

            if (isReset) {
              this.posts = [];
            }

            if (response.status === 200) {
              const responseData = parseData(response.data, IronMaidenListSchema) as any;
              const items = responseData.items ?? [];
              trimWpDomainInPlace(items, ['author.profile', 'categories.url']);

              const newPosts = items.filter(
                (post: any) => !this.posts.some((existing: any) => existing.id === post.id),
              );
              this.posts.push(...newPosts);

              this.posts.forEach((post, index, posts) => {
                const indexInFavorites = this.items.findIndex(
                  (item) => Number(item.post_id) === Number(post.id),
                );
                if (indexInFavorites > -1 && indexInFavorites < posts.length) {
                  [posts[indexInFavorites], posts[index]] = [posts[index], posts[indexInFavorites]];
                }
              });
            }

            this.articlesStore.fetchCommentsCount(includes);
            this.articlesStore.fetchViewsCount(includes);
            this.articlesStore.fetchAndSetFavorites({
              post_ids: includes.join(),
              per_page: includes.length,
            });

            this.updateState();
          })
          .catch(() => {
            this.state = 'error';
          });
      },

      onChooseFolder(folder: IFolderItem): void {
        this.folderCurrent = folder;
      },

      onCreateFolder(nameFolder: string): void {
        const toCurrent = this.folders.find((folder: IFolderItem) => folder.name === nameFolder);

        if (toCurrent) {
          this.folderCurrent = toCurrent;
        }
      },

      onClickArticleCard(article: IArticle, order: number): void {
        this.$sendYandexMetrika({
          level1: 'Личный кабинет_переход к материалу из избранного',
          level4: article.url,
          level5: order,
          level8: article.teaserUrl ? 'Тизер' : 'Списочная карточка',
        });
      },

      updateState(): void {
        const noData = this.total === 0;
        const allPostsLoaded = this.items.length === this.total;
        if (noData) {
          this.state = 'no-data';
        } else if (allPostsLoaded) {
          this.state = 'fulfilled';
        } else {
          this.state = 'pending';
        }
      },
    },
  });
</script>

<style lang="scss" scoped>
  .favorites {
    :deep(.lh-article-card-meta__container) {
      // Ограничение в одну строку
      max-height: 18px;
    }

    &__error {
      padding: 16px;

      @include tablet {
        padding: 24px;
      }
    }

    &__no-data {
      width: 100%;
      padding: 40px 0 80px;
      text-align: center;

      @include tablet {
        padding: 80px 0 120px;
      }
    }

    &__select-articles-button {
      text-decoration: none;
      display: inline-block;
      padding: 8px 24px;
      border-radius: 2px;
      @include fontTextBtn;
      color: white;
      background-color: $linkColor;
      margin-top: 24px;

      @include hover {
        background-color: #264eb1;
      }
    }
  }
</style>

<style lang="scss">
  // костыль
  .favorites {
    &.profile-content {
      .posts__item {
        .lh-favorite-block {
          .lh-context-menu__handler {
            .lh-button-icon__text {
              max-width: 105px;
              display: none;

              @include tablet {
                display: block;
              }
            }
          }
        }
      }
    }
  }
</style>
